import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {EChartsOption} from 'echarts';
import {TableRowCollapseEvent, TableRowExpandEvent} from 'primeng/table';
import {
    CampaignDto,
    CampaignGroupStatsDto,
    DetailCampaignDTO,
    EngineCampaignGroupService,
    EngineCampaignService
} from '@tj/tj-dmp-lib-api';

@Component({
    selector: 'dmp-campaign-group-stats',
    templateUrl: './campaign-group-stats.component.html',
    styleUrls: ['./campaign-group-stats.component.scss'],
    standalone: false
})
export class CampaignGroupStatsComponent implements OnChanges {

    @Input() tenant: string;
    @Input() groupId: number;

    loading = false;
    stats: CampaignGroupStatsDto;
    fluxChartOptions: EChartsOption;
    dataTypeChartOptions: EChartsOption;
    budgetChartOptions: EChartsOption;
    expandedRowKeys: Record<string, boolean> = {};
    campaignDetails: Record<number, DetailCampaignDTO> = {};
    detailLoading: Record<number, boolean> = {};

    constructor(
        private readonly engineCampaignGroupService: EngineCampaignGroupService,
        private readonly engineCampaignService: EngineCampaignService,
        private readonly translate: TranslateService,
        private readonly router: Router
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes.tenant || changes.groupId) && this.tenant && this.groupId) {
            this.expandedRowKeys = {};
            this.campaignDetails = {};
            this.detailLoading = {};
            this.loadStats();
        }
    }

    loadStats(): void {
        this.loading = true;
        this.engineCampaignGroupService.getStats(this.tenant, this.groupId).subscribe({
            next: (stats) => {
                this.stats = stats;
                this.buildCharts();
                this.loading = false;
            },
            error: () => {
                this.stats = null;
                this.loading = false;
            }
        });
    }

    onRowExpand(event: TableRowExpandEvent): void {
        const key = String(event.data.id);
        this.expandedRowKeys = {...this.expandedRowKeys, [key]: true};
        this.loadCampaignDetail(event.data.id);
    }

    onRowCollapse(event: TableRowCollapseEvent): void {
        const key = String(event.data.id);
        const next = {...this.expandedRowKeys};
        delete next[key];
        this.expandedRowKeys = next;
    }

    loadCampaignDetail(campaignId: number): void {
        if (this.campaignDetails[campaignId] || this.detailLoading[campaignId]) {
            return;
        }
        this.detailLoading[campaignId] = true;
        this.engineCampaignService.get(this.tenant, campaignId).subscribe({
            next: (detail) => {
                this.campaignDetails[campaignId] = detail;
                this.detailLoading[campaignId] = false;
            },
            error: () => {
                this.detailLoading[campaignId] = false;
            }
        });
    }

    openCampaign(campaignId: number, event?: Event): void {
        event?.stopPropagation();
        this.router.navigate(['/h/marketing/campaign', this.tenant, campaignId]);
    }

    fluxLabel(flux?: string): string {
        return flux ? this.translate.instant(`campaign.flux_${flux}`) : '—';
    }

    static isExportFlux(flux?: string): boolean {
        return flux === 'EXPORT_SCENARIO' || flux === 'EXPORT_DIRECT';
    }

    hasImportCampaigns(): boolean {
        return (this.stats?.campaigns || []).some(c => c.flux === 'IMPORT');
    }

    hasExportCampaigns(): boolean {
        return (this.stats?.campaigns || []).some(c => CampaignGroupStatsComponent.isExportFlux(c.flux));
    }

    financialSummary(flux: 'import' | 'export'): {
        count: number;
        costInitial: number;
        incremental: number;
        objective: number;
        consumed: number;
    } {
        const campaigns = (this.stats?.campaigns || []).filter(c =>
            flux === 'import' ? c.flux === 'IMPORT' : CampaignGroupStatsComponent.isExportFlux(c.flux));
        return campaigns.reduce<{ count: number; costInitial: number; incremental: number; objective: number; consumed: number }>((acc, c) => ({
            count: acc.count + 1,
            costInitial: acc.costInitial + (c.costInitial ?? 0),
            incremental: acc.incremental + (c.incrementalBudget ?? 0),
            objective: acc.objective + (c.objectiveBudgetMax ?? 0),
            consumed: acc.consumed + this.budgetConsumed(c),
        }), { count: 0, costInitial: 0, incremental: 0, objective: 0, consumed: 0 });
    }

    finCard(key: 'costInitialTotal' | 'incrementalTotal' | 'objectiveTotal' | 'observedTotal', flux: 'import' | 'export'): string {
        const suffix = flux === 'export' ? 'Revenue' : 'Cost';
        return this.translate.instant(`campaign.groupStats.card.${key}${suffix}`);
    }

    observedTotalLabel(flux?: string): string {
        if (CampaignGroupStatsComponent.isExportFlux(flux)) {
            return this.translate.instant('campaign.groupStats.card.observedTotalRevenue');
        }
        return this.translate.instant('campaign.groupStats.card.observedTotalCost');
    }

    tableObservedTotalHeader(): string {
        switch (this.groupFluxKind()) {
            case 'export':
                return this.translate.instant('campaign.groupStats.table.observedTotalRevenue');
            case 'mixed':
                return this.translate.instant('campaign.groupStats.table.observedTotalMixed');
            default:
                return this.translate.instant('campaign.groupStats.table.observedTotalCost');
        }
    }

    groupFluxKind(): 'import' | 'export' | 'mixed' {
        const campaigns = this.stats?.campaigns || [];
        let hasImport = false;
        let hasExport = false;
        for (const c of campaigns) {
            if (c.flux === 'IMPORT') {
                hasImport = true;
            } else if (CampaignGroupStatsComponent.isExportFlux(c.flux)) {
                hasExport = true;
            }
        }
        if (hasImport && hasExport) {
            return 'mixed';
        }
        if (hasExport) {
            return 'export';
        }
        return 'import';
    }

    costTrackingSectionTitle(flux?: string): string {
        if (flux) {
            return CampaignGroupStatsComponent.isExportFlux(flux)
                ? this.translate.instant('campaign.groupStats.trackingTitleRevenue')
                : this.translate.instant('campaign.groupStats.trackingTitleCost');
        }
        switch (this.groupFluxKind()) {
            case 'export':
                return this.translate.instant('campaign.groupStats.trackingTitleRevenue');
            case 'mixed':
                return this.translate.instant('campaign.groupStats.trackingTitleMixed');
            default:
                return this.translate.instant('campaign.groupStats.trackingTitleCost');
        }
    }

    alertCostThresholdLabel(flux?: string): string {
        const key = CampaignGroupStatsComponent.isExportFlux(flux) ? 'revenue' : 'cost';
        return this.translate.instant(`campaign.groupStats.alertCostThreshold.${key}`);
    }

    t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(key, params);
    }

    statusLabel(status?: string): string {
        return status ? this.translate.instant(`campaign.status.${status}`) : '—';
    }

    dataTypeLabel(dataType?: string): string {
        return dataType ? this.translate.instant(`campaign.dataType.${dataType}`) : '—';
    }

    campaignModeLabel(row: { campaignMode?: string; dataType?: string }): string {
        if (!row?.campaignMode) {
            return '—';
        }
        if (row.campaignMode === 'UNITS_MAX') {
            const key = row.dataType === 'ORDERS' ? 'UNITS_MAX_ORDERS' : 'UNITS_MAX_CONTACTS';
            return this.translate.instant(`campaign.campaignMode.${key}`);
        }
        return this.translate.instant(`campaign.campaignMode.${row.campaignMode}`);
    }

    isSpreadExpensePreview(detail: DetailCampaignDTO): boolean {
        return detail?.campaignDto?.campaignMode === 'SPREAD_EXPENSE'
            && detail?.campaignDto?.status !== CampaignDto.StatusEnum.TERMINATED;
    }

    showUnitsAlertProgress(detail: DetailCampaignDTO): boolean {
        const dto = detail?.campaignDto;
        return dto?.campaignMode === CampaignDto.CampaignModeEnum.UNITSMAX
            && dto.alertThresholdUnits != null
            && detail?.unitsAppliedRatio != null;
    }

    unitsProgressBarWidth(detail: DetailCampaignDTO): number {
        const r = detail?.unitsAppliedRatio;
        if (r == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, r));
    }

    unitsOverAlertThreshold(detail: DetailCampaignDTO): boolean {
        const r = detail?.unitsAppliedRatio;
        const t = detail?.campaignDto?.alertThresholdUnits;
        if (r == null || t == null) {
            return false;
        }
        return r >= t;
    }

    showBudgetAlertProgress(detail: DetailCampaignDTO): boolean {
        const dto = detail?.campaignDto;
        return dto?.campaignMode === CampaignDto.CampaignModeEnum.BUDGETMAX
            && dto.alertThresholdCost != null
            && detail?.budgetConsumedRatio != null;
    }

    budgetProgressBarWidth(detail: DetailCampaignDTO): number {
        const r = detail?.budgetConsumedRatio;
        if (r == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, r));
    }

    budgetOverAlertThreshold(detail: DetailCampaignDTO): boolean {
        const r = detail?.budgetConsumedRatio;
        const t = detail?.campaignDto?.alertThresholdCost;
        if (r == null || t == null) {
            return false;
        }
        return r >= t;
    }

    alertThresholdMarkerLeft(threshold: number | undefined | null): number {
        if (threshold == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, threshold));
    }

    formatDate(value?: string): string {
        if (!value) {
            return '—';
        }
        return new Date(value).toLocaleDateString('fr-FR');
    }

    formatCurrency(value?: number): string {
        if (value == null) {
            return '—';
        }
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
    }

    formatPercent(value?: number): string {
        if (value == null) {
            return '—';
        }
        return `${value.toFixed(2)} %`;
    }

    formatRatio(value?: number | null): string {
        if (value == null) {
            return '0.00';
        }
        return Number(value).toFixed(2);
    }

    unitsLabel(dataType?: string): string {
        return dataType === 'ORDERS'
            ? this.translate.instant('campaign.filter.dataType.orders').toLowerCase()
            : this.translate.instant('campaign.filter.dataType.contacts').toLowerCase();
    }

    budgetConsumed(row: { incrementalBudget?: number; costInitial?: number; flux?: string; campaignMode?: string }): number {
        const incremental = row.incrementalBudget ?? 0;
        const initial = row.costInitial ?? 0;
        if (row.campaignMode === 'SPREAD_EXPENSE' && CampaignGroupStatsComponent.isExportFlux(row.flux)) {
            return incremental;
        }
        if (CampaignGroupStatsComponent.isExportFlux(row.flux)) {
            return incremental - initial;
        }
        return incremental + initial;
    }

    budgetConsumedFromDetail(detail: DetailCampaignDTO): number {
        const incremental = detail.incrementalBudget ?? 0;
        const initial = detail.campaignDto?.costInitial ?? 0;
        if (detail.campaignDto?.campaignMode === 'SPREAD_EXPENSE'
            && CampaignGroupStatsComponent.isExportFlux(detail.campaignDto?.flux)) {
            return incremental;
        }
        if (CampaignGroupStatsComponent.isExportFlux(detail.campaignDto?.flux)) {
            return incremental - initial;
        }
        return incremental + initial;
    }

    detailBudgetContact(detail: DetailCampaignDTO): number | null {
        const value = (detail as DetailCampaignDTO & { incrementalBudgetContact?: number }).incrementalBudgetContact;
        return value != null ? value : null;
    }

    detailBudgetOrder(detail: DetailCampaignDTO): number | null {
        const value = (detail as DetailCampaignDTO & { incrementalBudgetOrder?: number }).incrementalBudgetOrder;
        return value != null ? value : null;
    }

    detailNewContacts(detail: DetailCampaignDTO): number | null {
        const value = (detail as DetailCampaignDTO & { newContactsApplied?: number }).newContactsApplied;
        return value != null ? value : null;
    }

    isOrderCampaign(dataType?: string): boolean {
        return dataType === 'ORDERS';
    }

    formatNewContacts(value?: number | null): string {
        if (value == null) {
            return '—';
        }
        return this.translate.instant('campaign.groupStats.newContacts', {count: value});
    }

    formatObjectiveBudget(value?: number | null): string {
        if (value == null) {
            return '—';
        }
        return this.formatCurrency(value);
    }

    formatObjectiveUnits(value?: number | null, dataType?: string): string {
        if (value == null) {
            return '—';
        }
        return `${value} ${this.unitsLabel(dataType)}`;
    }

    get budgetChartHeight(): number {
        const count = this.stats?.campaigns?.length || 1;
        return Math.max(340, Math.min(480, 280 + count * 8));
    }

    private static readonly FLUX_COLORS: Record<string, string> = {
        IMPORT: '#10b981',
        EXPORT: '#f59e0b',
        EXPORT_DIRECT: '#f59e0b',
        EXPORT_SCENARIO: '#ea580c'
    };

    private static readonly UNITS_COLORS: Record<string, string> = {
        ORDERS: '#8b5cf6',
        NEW_CONTACTS: '#2563eb',
        EXISTING_CONTACTS: '#0ea5e9'
    };

    private buildCharts(): void {
        this.fluxChartOptions = this.buildFluxPieChart();
        this.dataTypeChartOptions = this.buildUnitsPieChart();
        this.budgetChartOptions = this.buildBudgetBarChart();
    }

    private fluxColor(flux?: string): string {
        if (!flux) {
            return '#94a3b8';
        }
        return CampaignGroupStatsComponent.FLUX_COLORS[flux] ?? '#94a3b8';
    }

    private isTerminatedStatus(status?: string): boolean {
        return status === CampaignDto.StatusEnum.TERMINATED
            || status === CampaignDto.StatusEnum.ARCHIVED;
    }

    private barItemStyle(flux?: string, status?: string): { color: string; opacity: number; borderColor: string; borderWidth: number } {
        const base = this.fluxColor(flux);
        const terminated = this.isTerminatedStatus(status);
        return {
            color: terminated ? this.adjustColor(base, -40) : base,
            opacity: terminated ? 1 : 0.72,
            borderColor: terminated ? this.adjustColor(base, -60) : this.adjustColor(base, -15),
            borderWidth: 1
        };
    }

    private fluxSortOrder(flux?: string): number {
        if (flux === 'IMPORT') {
            return 0;
        }
        if (flux === 'EXPORT_DIRECT') {
            return 1;
        }
        if (flux === 'EXPORT_SCENARIO') {
            return 2;
        }
        return 3;
    }

    private sortCampaignsForChart(campaigns: NonNullable<CampaignGroupStatsDto['campaigns']>): NonNullable<CampaignGroupStatsDto['campaigns']> {
        return [...campaigns].sort((a, b) => {
            const fluxDiff = this.fluxSortOrder(a.flux) - this.fluxSortOrder(b.flux);
            if (fluxDiff !== 0) {
                return fluxDiff;
            }
            const dateA = a.dateStart ? new Date(a.dateStart).getTime() : 0;
            const dateB = b.dateStart ? new Date(b.dateStart).getTime() : 0;
            if (dateA !== dateB) {
                return dateA - dateB;
            }
            return (a.id ?? 0) - (b.id ?? 0);
        });
    }

    private adjustColor(hex: string, amount: number): string {
        const normalized = hex.replace('#', '');
        const num = parseInt(normalized, 16);
        const r = Math.min(255, Math.max(0, (num >> 16) + amount));
        const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
        const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }

    private breakdownAmount(item: { amount?: number; count?: number }): number {
        return item.amount ?? item.count ?? 0;
    }

    private fluxSliceLabel(key?: string): string {
        if (key === 'IMPORT') {
            return this.translate.instant('campaign.groupStats.chart.costLabel');
        }
        if (key === 'EXPORT') {
            return this.translate.instant('campaign.groupStats.chart.revenueLabel');
        }
        return this.fluxLabel(key);
    }

    private buildFluxPieChart(): EChartsOption {
        const breakdown = this.stats?.countByFlux || [];
        const data = breakdown.map(item => ({
            name: this.fluxSliceLabel(item.key),
            value: this.breakdownAmount(item),
            itemStyle: { color: this.fluxColor(item.key) }
        }));
        return {
            title: { text: this.translate.instant('campaign.groupStats.chart.fluxTitle'), left: 'center', textStyle: { fontSize: 14 } },
            tooltip: {
                trigger: 'item',
                valueFormatter: (value) => this.formatCurrency(Number(value))
            },
            legend: { bottom: 0 },
            color: [CampaignGroupStatsComponent.FLUX_COLORS.IMPORT, CampaignGroupStatsComponent.FLUX_COLORS.EXPORT],
            series: [{
                type: 'pie',
                radius: ['35%', '65%'],
                data: data.length > 0 ? data : [{ name: this.translate.instant('campaign.groupStats.chart.noData'), value: 0, itemStyle: { color: '#94a3b8' } }]
            }]
        };
    }

    private buildUnitsPieChart(): EChartsOption {
        const breakdown = this.stats?.countByUnits || [];
        const data = breakdown.map(item => ({
            name: item.label || item.key,
            value: item.count || 0,
            itemStyle: { color: CampaignGroupStatsComponent.UNITS_COLORS[item.key ?? ''] ?? '#94a3b8' }
        }));
        return {
            title: { text: this.translate.instant('campaign.groupStats.chart.unitsTitle'), left: 'center', textStyle: { fontSize: 14 } },
            tooltip: { trigger: 'item' },
            legend: { bottom: 0 },
            series: [{
                type: 'pie',
                radius: ['35%', '65%'],
                data: data.length > 0 ? data : [{ name: this.translate.instant('campaign.groupStats.chart.noData'), value: 0, itemStyle: { color: '#94a3b8' } }]
            }]
        };
    }

    private budgetChartTitle(): string {
        switch (this.groupFluxKind()) {
            case 'export':
                return this.translate.instant('campaign.groupStats.chart.budgetTitleRevenue');
            case 'mixed':
                return this.translate.instant('campaign.groupStats.chart.budgetTitleMixed');
            default:
                return this.translate.instant('campaign.groupStats.chart.budgetTitleCost');
        }
    }

    private buildBudgetBarChart(): EChartsOption {
        const campaigns = this.sortCampaignsForChart(this.stats?.campaigns || []);

        return {
            title: {
                text: this.budgetChartTitle(),
                subtext: this.translate.instant('campaign.groupStats.chart.budgetSubtitle'),
                left: 'center',
                textStyle: { fontSize: 14 },
                subtextStyle: { fontSize: 11, color: '#6b7280' }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: unknown) => {
                    const item = Array.isArray(params) ? params[0] : params as { dataIndex: number; value: number };
                    const campaign = campaigns[item.dataIndex];
                    if (!campaign) {
                        return '';
                    }
                    const status = this.isTerminatedStatus(campaign.status)
                        ? this.translate.instant('campaign.groupStats.chart.terminated')
                        : this.translate.instant('campaign.groupStats.chart.inProgress');
                    return [
                        `<strong>${campaign.code || campaign.name}</strong>`,
                        `${this.fluxLabel(campaign.flux)} · ${this.statusLabel(campaign.status)} (${status})`,
                        `${this.observedTotalLabel(campaign.flux)} : ${this.formatCurrency(Number(item.value))}`,
                        this.translate.instant('campaign.groupStats.chart.incremental', {
                            value: this.formatCurrency(campaign.incrementalBudget ?? 0)
                        })
                    ].join('<br/>');
                }
            },
            grid: { left: '3%', right: '4%', bottom: campaigns.length > 6 ? '22%' : '14%', containLabel: true },
            xAxis: {
                type: 'category',
                data: campaigns.map(c => c.code || c.name),
                axisLabel: {
                    rotate: campaigns.length > 4 ? 35 : 0,
                    interval: 0,
                    fontSize: 11
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: { formatter: (value: number) => `${value} €` }
            },
            series: [{
                type: 'bar',
                barMaxWidth: 48,
                data: campaigns.map(c => ({
                    value: this.budgetConsumed(c),
                    itemStyle: this.barItemStyle(c.flux, c.status)
                }))
            }]
        };
    }
}
